/**
 * Photo Upload Module for InterPals Private Messages
 * Handles file validation, preview, upload, and progress tracking
 * Supports multiple photo queue for batch sending
 *
 * Uses shared core from photo-upload-core.js for validation and upload logic
 */

import {
  PhotoUploadConfig as CoreConfig,
  PhotoUploadErrors as CoreErrors,
  validateFile,
  uploadPhoto as coreUploadPhoto,
  deletePhoto as coreDeletePhoto
} from '../modules/photo-upload-core.js';
import { t } from '../modules/i18n.js';

// Extended config for PM-specific UI settings
const PhotoUploadConfig = {
  ...CoreConfig,
  MAX_PHOTOS: 10,
  PREVIEW_MAX_HEIGHT: 128, // max-h-32 = 128px
  SCROLL_DELAY: 200,
  FOCUS_DELAY: 200,
  PREVIEW_SIZE: '300x400' // nginx allowed size
};

// Extended errors for PM-specific messages
const PhotoUploadErrors = {
  ...CoreErrors,
  get SEND_FAILED() { return t('common.photo_send_failed'); },
  get UPLOAD_IN_PROGRESS() { return t('common.photo_upload_in_progress'); },
  get FILE_READ_ERROR() { return t('common.photo_file_read_error'); }
};

/**
 * File validator utility class (wraps core validateFile)
 */
class FileValidator {
  static validate(file) {
    if (!file) {
      return [];
    }
    const result = validateFile(file);
    return result.valid ? [] : [result.error];
  }
}

/**
 * Progress tracker for upload operations
 */
class UploadProgressTracker {
  constructor(elements) {
    this.elements = elements;
    this.isVisible = false;
  }

  show() {
    this.elements.photoUploadProgress.removeClass('hidden');
    this.elements.photoProgressBar.css('width', '0%');
    this.elements.uploadStatusText.text(t('common.photo_uploading'));
    this.elements.photoUploadProgress.attr('aria-valuenow', '0');
    this.isVisible = true;
  }

  hide() {
    this.elements.photoUploadProgress.addClass('hidden');
    this.elements.photoUploadProgress.removeAttr('aria-valuenow');
    this.isVisible = false;
  }

  updateProgress(percentage) {
    if (!this.isVisible) return;

    const roundedPercentage = Math.round(percentage);
    this.elements.photoProgressBar.css('width', percentage + '%');
    this.elements.photoUploadProgress.attr('aria-valuenow', roundedPercentage);

    if (percentage >= 100) {
      this.elements.uploadStatusText.text(t('common.photo_processing'));
    }
  }

  setError(message) {
    if (this.isVisible) {
      this.elements.uploadStatusText.text(t('common.photo_upload_failed'));
    }
  }

  setComplete() {
    if (this.isVisible) {
      this.elements.uploadStatusText.text(t('common.photo_upload_complete'));
    }
  }
}

/**
 * Main photo upload manager
 */
class PhotoUploadManager {
  constructor() {
    this.photoQueue = [];
    this.entryCounter = 0;
    this.elements = {};
    this.progressTracker = null;

    this.init();
  }

  init() {
    this.cacheElements();
    this.initializeComponents();
    this.bindEvents();
    this.updateCameraButtonVisibility();
  }

  cacheElements() {
    this.elements = {
      photoInput: $('#photo_input'),
      photoPreviewContainer: $('#photo_preview_container'),
      photoPreviewList: $('#photo_preview_list'),
      photoUploadProgress: $('#photo_upload_progress'),
      photoProgressBar: $('#photo_progress_bar'),
      uploadStatusText: $('#upload_status_text'),
      messageTextarea: $('#message'),
      cameraBtn: $('#camera_btn'),
      msgSubmit: $('#msg_submit'),
      sendButtonText: $('#send_button_text')
    };
  }

  initializeComponents() {
    this.progressTracker = new UploadProgressTracker(this.elements);
  }

  bindEvents() {
    // File selection (supports multiple files)
    this.elements.photoInput.on('change', this.handleFileSelection.bind(this));

    // Remove individual photo via event delegation
    this.elements.photoPreviewList.on('click', '.photo-remove-btn', (event) => {
      const entryId = $(event.currentTarget).data('photo-id');
      this.removePhoto(entryId);
    });

    // Retry failed upload via event delegation
    this.elements.photoPreviewList.on('click', '.photo-retry-btn', (event) => {
      const entryId = $(event.currentTarget).data('photo-id');
      this.retryUpload(entryId);
    });

    // Message textarea changes
    this.elements.messageTextarea.on('input', this.updateCameraButtonVisibility.bind(this));

    // Clipboard paste image support
    this.elements.messageTextarea.on('paste', this.handlePaste.bind(this));

    // Progress updates
    $(document).on('photo-upload-progress', (event, percentage) => {
      this.progressTracker.updateProgress(percentage);
    });
  }

  async handleFileSelection(event) {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    for (const file of files) {
      await this.handleFile(file);
    }

    // Reset file input so the same files can be re-selected
    this.elements.photoInput.val('');
  }

  async handleFile(file) {
    if (!file) return;

    try {
      if (this.photoQueue.length >= PhotoUploadConfig.MAX_PHOTOS) {
        throw new Error(t('common.post_max_photos_error', { count: PhotoUploadConfig.MAX_PHOTOS }));
      }

      this.validateFile(file);

      const entryId = 'photo_' + (++this.entryCounter);
      const previewUrl = URL.createObjectURL(file);

      const entry = {
        id: entryId,
        file,
        previewUrl,
        photoId: null,
        uploadPromise: null,
        uploadStatus: 'pending' // pending | uploading | done | error
      };
      this.photoQueue.push(entry);

      this.renderThumbnail(entry);
      this.updatePreviewContainerVisibility();
      this.updateCameraButtonVisibility();
      this.updateSendButtonState();

      this.startEagerUpload(entry);
    } catch (error) {
      this.showError(error.message);
    }
  }

  startEagerUpload(entry) {
    entry.uploadStatus = 'uploading';
    this.updateThumbnailStatus(entry);

    entry.uploadPromise = coreUploadPhoto({
      file: entry.file,
      onProgress: () => {}
    }).then((result) => {
      entry.photoId = String(result.id);
      entry.uploadStatus = 'done';
      this.updateThumbnailStatus(entry);
      return entry.photoId;
    }).catch((error) => {
      console.error('Eager upload failed for', entry.id, error);
      entry.uploadStatus = 'error';
      this.updateThumbnailStatus(entry);
      throw error;
    });
  }

  retryUpload(entryId) {
    const entry = this.photoQueue.find(e => e.id === entryId);
    if (entry && entry.uploadStatus === 'error') {
      this.startEagerUpload(entry);
    }
  }

  updateThumbnailStatus(entry) {
    const $thumb = this.elements.photoPreviewList.find(`[data-photo-entry="${entry.id}"]`);
    if (!$thumb.length) return;

    // Remove existing status overlays
    $thumb.find('.upload-status-overlay').remove();

    if (entry.uploadStatus === 'uploading') {
      $thumb.append(`
        <div class="upload-status-overlay absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
          <div class="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      `);
    } else if (entry.uploadStatus === 'error') {
      $thumb.append(`
        <div class="upload-status-overlay absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
          <button type="button" class="photo-retry-btn text-white hover:text-red-200 flex flex-col items-center gap-0.5"
                  data-photo-id="${entry.id}" title="${t('common.retry')}">
            <i class="fa fa-exclamation-circle text-lg"></i>
            <span class="text-[10px]">${t('common.retry')}</span>
          </button>
        </div>
      `);
    } else if (entry.uploadStatus === 'done') {
      $thumb.append(`
        <div class="upload-status-overlay absolute inset-0 flex items-end justify-end p-1 pointer-events-none">
          <div class="h-4 w-4 rounded-full bg-green-500 flex items-center justify-center">
            <i class="fa fa-check text-white text-[8px]"></i>
          </div>
        </div>
      `);
    }
  }

  async handlePaste(event) {
    const clipboardData = event.clipboardData || event.originalEvent?.clipboardData;
    if (!clipboardData || !clipboardData.items) return;

    const imageItems = Array.from(clipboardData.items).filter(
      item => item.kind === 'file' && item.type.startsWith('image/')
    );

    if (imageItems.length === 0) return;

    event.preventDefault();

    for (const imageItem of imageItems) {
      const file = imageItem.getAsFile();
      if (file) {
        await this.handleFile(file);
      }
    }
  }

  renderThumbnail(entry) {
    const removeLabel = t('common.photo_remove');
    const previewLabel = t('common.photo_preview_alt');
    const $thumb = $(`
      <div class="relative inline-block flex-shrink-0" data-photo-entry="${entry.id}">
        <img src="${entry.previewUrl}" alt="${previewLabel}" class="h-20 w-20 object-cover rounded-lg">
        <button type="button" class="photo-remove-btn absolute -top-2 -right-2 h-6 w-6 rounded-full bg-gray-700/80 text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200 flex items-center justify-center"
                data-photo-id="${entry.id}"
                title="${removeLabel}"
                aria-label="${removeLabel}">
          <i class="fa fa-times text-sm" aria-hidden="true"></i>
        </button>
      </div>
    `);
    this.elements.photoPreviewList.append($thumb);
  }

  updatePreviewContainerVisibility() {
    if (this.photoQueue.length > 0) {
      this.elements.photoPreviewContainer.removeClass('hidden');
    } else {
      this.elements.photoPreviewContainer.addClass('hidden');
    }
  }

  validateFile(file) {
    const errors = FileValidator.validate(file);
    if (errors.length > 0) {
      throw new Error(errors[0]);
    }
  }

  async uploadPhoto(file, onProgress) {
    // Check if this file was already eagerly uploaded
    const entry = this.photoQueue.find(e => e.file === file);
    if (entry) {
      if (entry.photoId) {
        if (onProgress) onProgress(100);
        return entry.photoId;
      }
      if (entry.uploadPromise) {
        return await entry.uploadPromise;
      }
    }

    // Fallback: upload now (shouldn't happen with eager upload)
    const result = await coreUploadPhoto({
      file,
      onProgress: (percent) => {
        if (onProgress) {
          onProgress(percent);
        } else {
          $(document).trigger('photo-upload-progress', percent);
        }
      }
    });
    return String(result.id);
  }

  updateCameraButtonVisibility() {
    const hasText = this.elements.messageTextarea.val().trim().length > 0;
    const hasPhoto = this.photoQueue.length > 0;

    if (hasText && !hasPhoto) {
      this.elements.cameraBtn.hide();
    } else {
      this.elements.cameraBtn.show();
    }
  }

  removePhoto(entryId) {
    const entry = this.photoQueue.find(e => e.id === entryId);
    if (entry) {
      URL.revokeObjectURL(entry.previewUrl);
      if (entry.photoId) {
        coreDeletePhoto(entry.photoId).catch(() => {});
      }
    }

    this.photoQueue = this.photoQueue.filter(e => e.id !== entryId);
    this.elements.photoPreviewList.find(`[data-photo-entry="${entryId}"]`).remove();

    this.updatePreviewContainerVisibility();
    this.updateCameraButtonVisibility();
    this.updateSendButtonState();
  }

  removeAllPhotos({ deleteFromServer = true } = {}) {
    for (const entry of this.photoQueue) {
      URL.revokeObjectURL(entry.previewUrl);
      if (deleteFromServer && entry.photoId) {
        coreDeletePhoto(entry.photoId).catch(() => {});
      }
    }

    this.photoQueue = [];
    this.elements.photoPreviewList.empty();
    this.elements.photoInput.val('');

    this.progressTracker.hide();

    this.updatePreviewContainerVisibility();
    this.updateCameraButtonVisibility();
    this.updateSendButtonState();
  }

  updateSendButtonState() {
    if (typeof window.updateSendButtonState === 'function') {
      window.updateSendButtonState();
    }
  }

  showError(message) {
    setTimeout(() => {
      alert(message);
    }, 100);
  }

  // Public API
  getSelectedFile() {
    return this.photoQueue.length > 0 ? this.photoQueue[0].file : null;
  }

  hasPhoto() {
    return this.photoQueue.length > 0;
  }

  getPhotoQueue() {
    return [...this.photoQueue];
  }

  getPhotoCount() {
    return this.photoQueue.length;
  }

  showProgress() {
    this.progressTracker.show();
  }

  hideProgress() {
    this.progressTracker.hide();
  }
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    PhotoUploadManager,
    PhotoUploadConfig,
    PhotoUploadErrors,
    FileValidator
  };
}

// Global initialization function for backward compatibility
function initPhotoUpload() {
  const photoUploadManager = new PhotoUploadManager();

  // Expose API for backward compatibility and external use
  window.photoUpload = {
    uploadPhoto: photoUploadManager.uploadPhoto.bind(photoUploadManager),
    getSelectedFile: photoUploadManager.getSelectedFile.bind(photoUploadManager),
    removePhoto: photoUploadManager.removePhoto.bind(photoUploadManager),
    removeAllPhotos: photoUploadManager.removeAllPhotos.bind(photoUploadManager),
    hasPhoto: photoUploadManager.hasPhoto.bind(photoUploadManager),
    handleFile: photoUploadManager.handleFile.bind(photoUploadManager),
    showProgress: photoUploadManager.showProgress.bind(photoUploadManager),
    hideProgress: photoUploadManager.hideProgress.bind(photoUploadManager),
    getPhotoQueue: photoUploadManager.getPhotoQueue.bind(photoUploadManager),
    getPhotoCount: photoUploadManager.getPhotoCount.bind(photoUploadManager),
    retryUpload: photoUploadManager.retryUpload.bind(photoUploadManager)
  };

  return photoUploadManager;
}

// Auto-initialize if jQuery is available
if (typeof $ !== 'undefined') {
  $(document).ready(() => {
    if ($(".privateMessages").length > 0) {
      initPhotoUpload();
    }
  });
}
